import { App, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getMenuItems } from './getMenuItems';
import { ActionsDropdown as ActionsDropdownBase } from '@/shared/components/ActionsDropdown';
import { agreementsQueryKeys, useDeleteAgreement, useRestoreAgreement } from '@/features/agreements/hooks/useAgreements';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';
import { ROUTES } from '@/App/Router/routes';

const { Text } = Typography;

export const ActionsDropdown = ({ record, compact = false }) => {
  const { notification, modal } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deleteAgreementMutation = useDeleteAgreement();
  const restoreAgreementMutation = useRestoreAgreement();

  const handleUpdateAgreement = () => {
    navigate(ROUTES.AGREEMENTS.UPDATE(record.id));
  };

  const handleViewAgreement = () => {
    navigate(ROUTES.AGREEMENTS.VIEW(record.id));
  };

  const handleActivateAgreement = () => {
    restoreAgreementMutation.mutate({ id: record.id }, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: agreementsQueryKeys.list() });
        void queryClient.invalidateQueries({ queryKey: agreementsQueryKeys.detail(record.id) });
        notification.success({
          message: <Text strong>Convenio activado</Text>,
          description: '¡El convenio se ha activado correctamente!',
        });
      },
      onError: (error) => {
        notification.error({
          message: <Text strong>Convenio no activado</Text>,
          description: getErrorMessage(error),
        });
      },
    });
  };

  const handleInactivateAgreement = () => {
    modal.confirm({
      title: '¿Seguro que deseas inactivar el convenio?',
      content: (
        <Text>
          El convenio dejará de estar disponible. Podrás activarlo más adelante si lo deseas.
        </Text>
      ),
      okText: 'Sí, inactivar',
      onOk: () => {
        deleteAgreementMutation.mutate({ id: record.id }, {
          onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: agreementsQueryKeys.list() });
            void queryClient.invalidateQueries({ queryKey: agreementsQueryKeys.detail(record.id) });
            notification.success({
              message: <Text strong>Convenio inactivado</Text>,
              description: '¡El convenio se ha inactivado correctamente!',
            });
          },
          onError: (error) => {
            notification.error({
              message: <Text strong>Convenio no inactivado</Text>,
              description: getErrorMessage(error),
            });
          },
        });
      },
    });
  };

  const actions = {
    updateAgreement: {
      handler: handleUpdateAgreement,
      loading: false,
    },
    viewAgreement: {
      handler: handleViewAgreement,
      loading: false,
    },
    activate: {
      handler: handleActivateAgreement,
      loading: restoreAgreementMutation.isPending,
    },
    inactivate: {
      handler: handleInactivateAgreement,
      loading: deleteAgreementMutation.isPending,
    },
  };

  const items = getMenuItems({ record, actions });

  return <ActionsDropdownBase items={items} compact={compact} />;
};
